import React, { useState } from "react";
import { Form } from "react-bootstrap";

import DstButton from "../../components/DstButton/DstButton";
import DstLiveTable from "../../components/DstTable/DstLiveTable";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import { useSession } from "../../contexts/SessionContext";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { getLegacyAdminHostname } from "../../services/UtilityService";
import UserRoleScopeModal from "./components/UserRoleScopeModal/UserRoleScopeModal";

const PermissionUserRolesScopes = () => {
  const { language } = useSession();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [onlyActive, setOnlyActive] = useState<boolean>(true);
  const [selectedUserRole, setSelectedUserRole] = useState<any | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const _renderScopeLinks = (items: any, basePath: string, language: string, legacy = false) => {
    if (items === null) return <span>{translate(language, "PAGES.USER_ROLES_SCOPES.VALUES_ALIAS.ALL")}</span>;
    if (!items?.length) return <span>{translate(language, "PAGES.USER_ROLES_SCOPES.VALUES_ALIAS.NONE")}</span>;
    const maxToShow = 3;
    const displayedItems = items?.slice(0, maxToShow);
    const remainingCount = items?.length - maxToShow;

    return (
      <span title={items?.map((item: any) => item.id).join(", ")}>
        {displayedItems?.map((item: any, index: number) => (
          <React.Fragment key={item.id}>
            <a
              href={legacy ? `${getLegacyAdminHostname()}/${basePath}/${item.id}` : `/${basePath}/${item.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none"
              onClick={(event) => event.stopPropagation()}
            >
              {item.display_name}
            </a>
            {index < displayedItems.length - 1 ? ", " : ""}
          </React.Fragment>
        ))}
        {remainingCount > 0 && (
          <span className="charcoal-gray-font">
            {displayedItems.length ? ", " : ""}+{remainingCount}
          </span>
        )}
      </span>
    );
  };

  const remoteUrl = [
    "users/roles/scopes?",
    `fetch_scopes_informations=true`,
    `&search=${encodeURIComponent(searchTerm)}`,
    onlyActive ? "&only_active=true" : "",
    `&_refresh=${refreshKey}`,
  ].join("");

  const columns = [
    {
      key: "user.email",
      name: translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.EMAIL"),
      sortable: true,
    },
    {
      key: "user.full_name",
      name: translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.FULL_NAME"),
      sortable: true,
      render: (item: any) => <React.Fragment>{`${item.user.first_name} ${item.user.last_name}`.trim()}</React.Fragment>,
    },
    {
      key: "role.name",
      name: translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.ROLE"),
      sortable: true,
    },
    {
      key: "users_scope",
      name: translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.USERS_SCOPE"),
      render: (item: any) => _renderScopeLinks(item.users_scope, "user", language),
    },
    {
      key: "cohorts_scope",
      name: translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.COHORTS_SCOPE"),
      render: (item: any) => _renderScopeLinks(item.cohorts_scope, "cohort", language),
    },
    {
      key: "project_groups_scope",
      name: translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.PROJECT_GROUPS_SCOPE"),
      render: (item: any) => _renderScopeLinks(item.project_groups_scope, "group_projects", language, true),
    },
    {
      key: "animators_scope",
      name: translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.ANIMATORS_SCOPE"),
      render: (item: any) => _renderScopeLinks(item.animators_scope, "user", language),
    },
    {
      key: "organizers_scope",
      name: translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.ORGANIZERS_SCOPE"),
      render: (item: any) => _renderScopeLinks(item.organizers_scope, "user", language),
    },
    {
      key: "dt_updated",
      name: translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.LAST_MODIFIED"),
      sortable: true,
      render: (item: any) => {
        return (
          <span title={formatDateHumanly(language, item.dt_updated || item.dt_added, true)}>
            {formatDateHumanly(language, item.dt_updated || item.dt_added)}
          </span>
        );
      },
    },
  ];

  return (
    <div className="px-3 pt-3">
      <h1 className="mb-3">{translate(language, "PAGES.USER_ROLES_SCOPES.TITLE")}</h1>
      <div className="mb-3 d-flex gap-3 align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <div style={{ maxWidth: 400 }}>
            <ListerSearch setSearchQuery={setSearchTerm} timeDelay={500} showSearchIcon />
          </div>
          <DstButton
            btnClass="h-100 d-flex align-items-center ms-3"
            btnImageBefore={
              <Form.Check type="switch" className="check-box s-bold me-2" checked={onlyActive} readOnly />
            }
            value={translate(language, "PAGES.USER_ROLES_SCOPES.FILTER_ONLY_ACTIVE")}
            variant="light"
            btnHeight="38px"
            btnWidth="220px"
            clickFunction={() => setOnlyActive((prev) => !prev)}
          />
        </div>
        <DstButton
          value={translate(language, "PAGES.USER_ROLES_SCOPES.ADD_SCOPE")}
          btnClass="btn btn-primary"
          clickFunction={() => {
            setIsEditMode(false);
            setSelectedUserRole(null);
            setShowModal(true);
          }}
          btnHeight="38px"
        />
      </div>

      <DstLiveTable
        columns={columns}
        remote={remoteUrl}
        remoteKey="user_roles_scopes"
        pagination
        onRowClick={(_, item) => {
          setIsEditMode(true);
          setSelectedUserRole(item);
          setShowModal(true);
        }}
      />

      {showModal && (
        <UserRoleScopeModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            setRefreshKey((key) => key + 1);
          }}
          userRole={isEditMode ? selectedUserRole : undefined}
          language={language}
        />
      )}
    </div>
  );
};

export default PermissionUserRolesScopes;
